/* global AppSettings, Mailcheck */
/**
 * Email validation
 *
 * @author Andy Lemaire
 */
import $ from 'jquery';
import EventTypes from 'components/EventTypes';
import createLogger from 'components/logger/Logger';
import Mustache from 'mustache';
import { getCSRFToken } from './utils/CsrfUtils';
import 'vendor/mailcheck/mailcheck';

const Logger = createLogger('emailValidation');

const hasSuggestionSelector = 'has-suggestion';
const mailcheckMessageClass = '.js-mailcheckMessage';
const mailcheckSuggestionClass = '.js-mailcheckSuggestion';
const mailcheckTemplate = `<span class="mailcheckMessage js-mailcheckMessage">{{{message}}} <span class="mailcheckSuggestion js-mailcheckSuggestion">{{{suggestion}}}</span></span>`;

let EmailValidator;

class EmailValidation {
  constructor(form, selector) {
    Logger.info('Initialized');

    this.form = form;
    this.selector = selector;
    this.inputs = this.form.querySelectorAll(this.selector);

    this.isInstoreConsumerRegistrationPage = !!document.querySelectorAll(
      'body.page-inStoreConsumerRegistrationPage'
    ).length;

    this.pushDomains();
    this.bindEvents();
  }

  bindEvents() {
    document.jq
      .on(EventTypes.EMAIL_VALIDATION_SUGGESTION, this.checkMail.bind(this))
      .on(EventTypes.FORM_INPUT_ERROR_SHOWN, this.bindLinks.bind(this));

    const onChange = event => this.onChange(event);
    const onSubmit = event => this.onSubmit(event);

    this.form.jq.on('submit', onSubmit);
    this.inputs.jq.on('change', onChange);
    this.inputs.jq.on('focus', async () => {
      this.csrfToken = await getCSRFToken();
    });

    if (this.isInstoreConsumerRegistrationPage) {
      this.form.jq.data('api').attachSubmitCallback(this.checkAvailability.bind(this), 1);
    }
  }

  appendSuggestion(element, suggestion) {
    element.classList.add(hasSuggestionSelector);
    element.insertAdjacentHTML('beforeend', suggestion);
  }

  applySuggestion(event) {
    const target = event.currentTarget;
    const formInputGroup = target.closest('.formInputGroup');
    this.removeSuggestion(formInputGroup);

    const input = formInputGroup.querySelector(this.selector);
    const suggestion = target.innerText;
    input.value = suggestion;

    input.jq.trigger('change');
  }

  /**
   * Check email address for availability
   */
  async checkAvailability() {
    const input = this.inputs[0];
    const emailAvailabilityUrl = input.getAttribute('data-email-availability-url') || false;

    if (!emailAvailabilityUrl) {
      return;
    }

    this.csrfToken = await getCSRFToken();

    const data = {
      email: input.value,
      CSRFToken: this.csrfToken,
    };

    const membershipCardNo = this.form.querySelector('#membershipCardNumber');
    if (membershipCardNo && membershipCardNo.value) {
      data.membershipCardId = membershipCardNo.value;
    }

    return $.ajax({
      type: 'POST',
      data,
      cache: false,
      url: `/${AppSettings.locale}${emailAvailabilityUrl}`,
    })
      .done(response => {
        if (!response.result && !!response.errorMessage) {
          document.jq
            .trigger(EventTypes.FORM_ELEMENT_ERROR_SHOW, [this.form, input, response.errorMessage])
            .trigger(EventTypes.FORM_INPUT_ERROR_SHOWN, [input]);
        }
      })
      .fail(error => {
        Logger.warn(`Unable to get email availability: ${error.status}`);
      });
  }

  /**
   * Check email address for suggestions
   */
  checkMail(event, input) {
    const formInputGroup = input.closest('.formInputGroup');

    Mailcheck.run({
      email: input.value,
      suggested: suggestion => {
        if (formInputGroup.classList.contains(hasSuggestionSelector)) {
          this.removeSuggestion(formInputGroup);
        }

        const message = window.labels.addressChecker.mailcheckSuggestion;
        const renderedSuggestion = Mustache.render(mailcheckTemplate, {
          message,
          suggestion: suggestion.full,
        });
        this.appendSuggestion(formInputGroup, renderedSuggestion);

        const mailcheckSuggestion = formInputGroup.querySelector(mailcheckSuggestionClass);
        mailcheckSuggestion.addEventListener('click', this.applySuggestion.bind(this));
      },
      empty: () => {
        if (formInputGroup.classList.contains(hasSuggestionSelector)) {
          this.removeSuggestion(formInputGroup);
        }
      },
    });
  }

  onChange(event) {
    const input = event.currentTarget;
    const prevErrorDescription = this.form.querySelector(
      `[id=errorDescription__${event.target.name}]`
    );
    const isEmailAnonymised = input.hasAttribute('data-anonymised-email');

    if (prevErrorDescription)
      document.jq.trigger(EventTypes.FORM_ELEMENT_ERROR_HIDE, [this.form, input]);

    if (isEmailAnonymised || input.placeholder === input.value) return;

    this.validate(input)
      .then(input => this.checkAvailability(input))
      .catch(message => Logger.error(message));
    document.jq.trigger(EventTypes.EMAIL_VALIDATION_SUGGESTION, input);
  }

  onSendCard(event) {
    const target = event.currentTarget;
    const formInputGroup = target.closest('.formInputGroup');

    if (target.classList.contains('js-sendEmail')) {
      event.preventDefault();

      return $.ajax({
        type: 'POST',
        data: {
          CSRFToken: this.csrfToken,
        },
        cache: false,
        url: target.href,
      })
        .done(response => {
          if (response.result) {
            const formInputError = formInputGroup.querySelector('.formInputError');

            if (formInputGroup && formInputError) {
              formInputGroup.removeChild(formInputError);
            }
          }
        })
        .fail(error => Logger.warn(`Send card email: ${error.status}`));
    }
  }

  onSubmit(event) {
    const hasBeenSubmitted = event.target.querySelector('.js-submit-button.loading');
    const hasErrors = this.form.querySelectorAll('.formInputGroup--email .error');

    if (hasBeenSubmitted) {
      hasBeenSubmitted.disabled = true;
      return;
    }

    this.inputs.forEach(input => input.jq.trigger('change'));

    if (hasErrors.length) {
      event.preventDefault();
    }
  }

  /**
   * Push extra domains to the suggestion list
   */
  pushDomains() {
    const defaultDomains = AppSettings.mailCheckDefaultDomains;
    if (defaultDomains.length) {
      if (Mailcheck.defaultDomains.indexOf(defaultDomains[0]) === -1) {
        Mailcheck.defaultDomains.push(...defaultDomains);
      }
    }
  }

  removeSuggestion(element) {
    element.classList.remove(hasSuggestionSelector);

    const suggestion = element.querySelector(mailcheckMessageClass);
    if (suggestion) {
      element.removeChild(suggestion);
    }
  }

  bindLinks(event, input) {
    const formInputGroup = input.closest('.formInputGroup');

    if (!formInputGroup) {
      return;
    }

    const links = formInputGroup.querySelectorAll('a');

    if (links.length) {
      const onSendCard = event => this.onSendCard(event);
      links.forEach(link => link.jq.on('click', onSendCard));
    }
  }

  /**
   * Replace {0} in a string to a value coming from an object
   */
  messageTemplate(label, data) {
    return label.replace(/\{[0-9]+\}/g, m0 => {
      return data[m0] || '';
    });
  }

  /**
   * Validate email address with Commons Validator
   */
  async validate(input) {
    if (!EmailValidator) {
      const module = await import(
        /* webpackChunkName: "vendor--commons-validator-js" */ 'commons-validator-js'
      );
      EmailValidator = module.EmailValidator;
    }
    return new Promise((resolve, reject) => {
      const validator = new EmailValidator();
      const re = /^\s*?(.+)@(.+?)\s*$/i;
      const isValid = validator.isValid(input.value) && re.test(input.value);
      if (isValid) {
        document.jq.trigger(EventTypes.FORM_ELEMENT_ERROR_HIDE, [this.form, input]);
        resolve(input);
      } else if (input.value === '') {
        document.jq.trigger(EventTypes.FORM_ELEMENT_ERROR_SHOW, [
          this.form,
          input,
          window.labels.forms[this.form.id]?.[`${input.id}Invalid`] ||
            this.messageTemplate(window.labels.forms.messageRequiredInput, {
              '{0}': window.labels.forms.labelEmail,
            }),
        ]);
      } else {
        document.jq.trigger(EventTypes.FORM_ELEMENT_ERROR_SHOW, [
          this.form,
          input,
          window.labels.emailValidation.invalid,
        ]);
        reject(window.labels.emailValidation.invalid);
      }
    });
  }
}

export default EmailValidation;
